import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../api';
import { NAV_CATEGORIES } from '../data/navCategories';
import { fetchNavigationPreferencesOnce, invalidateNavigationPreferencesCache, navigationPreferencesKey } from './navigationPreferencesCache';

const STORAGE_KEY = 'kp-collapsed-sections';
const SYNC_DEBOUNCE_MS = 600;

function defaultCollapsedKeys() {
  return NAV_CATEGORIES.filter((c) => c.defaultCollapsed).map((c) => c.key);
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.global)) return parsed;
    }
  } catch {}
  return null;
}

function initialState() {
  return readStored() ?? { global: defaultCollapsedKeys(), project: ['management', 'finance', 'tools', 'admin'] };
}

export function useCollapsedSections() {
  const [state, setState] = useState(() => initialState());
  const hydratedRef = useRef(false);
  const syncTimerRef = useRef(null);
  // Det senast kända servervärdet. Vid uppstart skrev hooken tidigare
  // tillbaka exakt det den just hämtat (en GET + en PUT per instans).
  const syncedKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchNavigationPreferencesOnce();
      if (cancelled) return;
      const cs = data?.collapsed_sections;
      if (cs && Array.isArray(cs.global)) {
        const next = {
          global: cs.global,
          project: Array.isArray(cs.project) ? cs.project : [],
        };
        syncedKeyRef.current = navigationPreferencesKey(next);
        setState(next);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cs)); } catch {}
      }
      hydratedRef.current = true;
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!hydratedRef.current) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
    if (navigationPreferencesKey(state) === syncedKeyRef.current) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      try {
        const prev = (await fetchNavigationPreferencesOnce()) || {};
        await fetch(`${API_URL}/users/me/navigation-preferences`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            theme: prev.theme || 'light',
            favorites: Array.isArray(prev.favorites) ? prev.favorites : [],
            recent_items: Array.isArray(prev.recent_items) ? prev.recent_items : [],
            collapsed_sections: state,
          }),
        });
        syncedKeyRef.current = navigationPreferencesKey(state);
        invalidateNavigationPreferencesCache();
      } catch {}
    }, SYNC_DEBOUNCE_MS);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [state]);

  const isCollapsed = useCallback(
    (key, mode = 'global') => (state[mode] || []).includes(key),
    [state],
  );

  const toggle = useCallback((key, mode = 'global') => {
    setState((cur) => {
      const list = cur[mode] || [];
      const next = list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
      return { ...cur, [mode]: next };
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setState({ global: defaultCollapsedKeys(), project: ['management', 'finance', 'tools', 'admin'] });
  }, []);

  return { state, isCollapsed, toggle, resetToDefaults };
}
